import { useEffect, useRef, useState } from 'react';
import { ApiError, NetworkError } from '@/lib/api-client';
import { refetchEntitlementCache } from '@/hooks/useEntitlement';
import { refetchSubscriptionStateCache } from '@/hooks/useSubscriptionState';
import { fetchStoryById } from './api';
import { isAssetPollingNeeded, type StoryDetail } from './types';

const POLL_INTERVAL_MS = 4_000;

export interface UseStoryDetailResult {
  story: StoryDetail | null;
  error: ApiError | NetworkError | null;
  isLoading: boolean;
  setStory: (next: StoryDetail) => void;
}

/**
 * Fetch a story by id and poll while audio / PDF are still generating.
 * Stops polling when both assets are terminal (completed / failed /
 * ready / not_requested) or when the component unmounts.
 *
 * Polling cadence: 4s — middle of the 3–5s target. Single in-flight
 * request per tick (no overlap guard needed since apiFetch serializes
 * via refresh token flow and we gate on `cancelled` before setState).
 *
 * The `setStory` escape hatch lets callers (e.g., favorite-toggle,
 * FIX A retrofit) apply an optimistic update without waiting for the
 * next poll tick. When retrofit flips `audioStatus`/`pdfStatus` to
 * `'pending'` via `setStory`, the polling effect below re-evaluates
 * `shouldPoll` (derived at render time from the current `story`) and
 * restarts the interval automatically. Before the FIX A follow-up
 * split, polling was fused into one effect keyed only on `storyId` —
 * so a text-only initial fetch cleared the interval immediately and
 * a later `setStory({...story, audioStatus:'pending'})` could not
 * restart it without a full route re-entry.
 */
export function useStoryDetail(storyId: string): UseStoryDetailResult {
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [error, setError] = useState<ApiError | NetworkError | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // B2 (Bug 2): true once this story has had an asset in flight, so we fire a
  // quota refresh exactly on the in-flight→terminal transition (not when an old,
  // already-terminal story is opened — nothing changed then).
  const hadPendingAssets = useRef(false);

  // ── Initial fetch ────────────────────────────────────────
  // Runs once per storyId change. Never restarts on status transitions.
  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    hadPendingAssets.current = false;
    setIsLoading(true);
    void (async () => {
      try {
        const next = await fetchStoryById(storyId);
        if (cancelled) return;
        setStory(next);
        setError(null);
        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError || err instanceof NetworkError) {
          setError(err);
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storyId]);

  // ── Polling loop ─────────────────────────────────────────
  // Starts when `shouldPoll` flips true (asset in transit) and stops
  // cleanly when it flips false (all terminal). Re-runs whenever the
  // in-transit flag toggles — that's what makes retrofit's optimistic
  // `setStory({...story, audioStatus:'pending'})` resume polling
  // without waiting for a route re-mount.
  const shouldPoll = story !== null && isAssetPollingNeeded(story);
  useEffect(() => {
    if (!storyId || !shouldPoll) return;
    hadPendingAssets.current = true;
    let cancelled = false;
    let intervalId: number | null = window.setInterval(() => {
      void (async () => {
        try {
          const next = await fetchStoryById(storyId);
          if (cancelled) return;
          setStory(next);
          setError(null);
          if (!isAssetPollingNeeded(next) && hadPendingAssets.current) {
            // Assets just settled (deduction + any refund committed) → refresh
            // the quota caches so the next Start/Account/abo mount shows the
            // correct remaining. Fire once, only on the in-flight → terminal
            // transition.
            hadPendingAssets.current = false;
            void refetchEntitlementCache();
            void refetchSubscriptionStateCache();
          }
        } catch (err) {
          if (cancelled) return;
          if (err instanceof ApiError || err instanceof NetworkError) {
            setError(err);
            // Stop on error — callers can refresh the route to retry.
            if (intervalId !== null) {
              window.clearInterval(intervalId);
              intervalId = null;
            }
          }
        }
      })();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
  }, [storyId, shouldPoll]);

  return { story, error, isLoading, setStory };
}
